import type { Dispatch, MutableRefObject, RefObject, SetStateAction } from "react";
import type { NavigateFunction } from "react-router-dom";
import { formatYmdLong } from "../../../../lib/dates";
import {
  autoDistanceLabel,
  catalogEventStartToYmd,
  formatEventLocation,
  type CatalogDistanceOption,
  type CatalogEventDTO
} from "../../../../lib/eventsApi";
import { journeyRaceName, type JourneyView } from "../../../../lib/journeyApi";
import type { WizardRace } from "../../../../lib/raceSources";
import { LucideIcon } from "../../../FG/LucideIcon";
import { ModalValHint } from "../../../modals/ModalValHint";
import { EventSearchField, type EventSearchFieldHandle } from "../../../Onboarding/EventSearchField";
import { raceSourceLabel } from "../formatting";
import type { WizardStepId } from "../wizardMeta";

/** The wizard's RACE step panel (race card, distance chips, search, exits). */
export function RaceStep({
  step,
  existingJourneyWithWorkouts,
  navigate,
  onClose,
  selectedRace,
  raceInPast,
  weeksOut,
  raceDate,
  raceSearchRef,
  raceFieldId,
  focusRaceSearchRef,
  query,
  setQuery,
  setSelectedRace,
  setRaceDistanceChoices,
  continueGate,
  distanceOptions,
  noPlansForDistance,
  openAddEvent,
  otherRaces,
  today,
  containingJourney,
  openMilestoneConfirm
}: {
  step: WizardStepId;
  existingJourneyWithWorkouts: JourneyView | null;
  navigate: NavigateFunction;
  onClose: () => void;
  selectedRace: WizardRace | null;
  raceInPast: boolean;
  weeksOut: number;
  raceDate: string | null;
  raceSearchRef: RefObject<EventSearchFieldHandle>;
  raceFieldId: string;
  focusRaceSearchRef: MutableRefObject<boolean>;
  query: string;
  setQuery: Dispatch<SetStateAction<string>>;
  setSelectedRace: Dispatch<SetStateAction<WizardRace | null>>;
  setRaceDistanceChoices: Dispatch<SetStateAction<CatalogDistanceOption[]>>;
  continueGate: { nudge: string | null; clearNudge: () => void };
  distanceOptions: CatalogDistanceOption[];
  noPlansForDistance: boolean;
  openAddEvent: (
    presetOrOpts:
      | string
      | {
          name?: string;
          eventDate?: string;
          initialLocation?: string;
          initialDistanceUnset?: boolean;
        }
  ) => void;
  otherRaces: WizardRace[];
  today: string;
  containingJourney: JourneyView | null;
  openMilestoneConfirm: () => void;
}) {
  return (
    <div className="fgp-wizard-panel fgp-wizard-panel--race-step">
      <div className="fgp-wizard-race-step-main">
        {existingJourneyWithWorkouts && (
          <div
            className="fgp-modal-callout fgp-modal-callout--purple"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 14
            }}>
            <span style={{ flex: "1 1 220px" }}>
              You already have workouts on your calendar for{" "}
              <strong>{journeyRaceName(existingJourneyWithWorkouts)}</strong> — want to go
              straight there instead?
            </span>
            <button
              type="button"
              className="fg-btn fg-btn--ghost fg-btn--sm"
              onClick={() => {
                navigate("/my-training");
                onClose();
              }}>
              Go to my calendar →
            </button>
          </div>
        )}
        <div className="fgp-modal-field-label">Your race</div>
        {selectedRace && (
          <div className={`fgp-wizard-race-card${raceInPast ? " fgp-wizard-race-card--bad" : ""}`}>
            <span className="fgo-event-opt__ico" aria-hidden>
              <LucideIcon name="flag" size={18} color="#B45309" strokeWidth={2} />
            </span>
            <span className="fgo-event-opt__body">
              <span className="fgo-event-opt__title">{selectedRace.name}</span>
              <span className="fgo-event-opt__meta">
                {formatYmdLong(selectedRace.dateYmd)}
                {raceInPast ? (
                  <span style={{ color: "var(--fg-danger-ink)" }}> · that date has passed — pick another</span>
                ) : weeksOut > 0 ? (
                  <> · {weeksOut} {weeksOut === 1 ? "week" : "weeks"} out</>
                ) : raceDate ? (
                  <> · less than a week away</>
                ) : null}
              </span>
              {raceSourceLabel(selectedRace.source) && (
                <span
                  className="fgo-event-opt__meta"
                  style={{ fontSize: 12, color: "var(--fg-gray-700)", opacity: 0.85 }}>
                  {raceSourceLabel(selectedRace.source)}
                </span>
              )}
            </span>
            <button
              type="button"
              className="fg-btn fg-btn--ghost fg-btn--sm"
              onClick={() => {
                focusRaceSearchRef.current = true;
                setSelectedRace(null);
                setRaceDistanceChoices([]);
                setQuery("");
                continueGate.clearNudge();
              }}>
              Change
            </button>
          </div>
        )}
        {selectedRace && distanceOptions.length > 0 && (
          <div className="fgp-wizard-race-distance">
            <div className="fgp-modal-field-label">
              {distanceOptions.length > 1 ? "Which distance?" : "Distance"}
            </div>
            <div className="fgm-ae-distances">
              {distanceOptions.map((d) => {
                const single = distanceOptions.length === 1;
                const on = single || selectedRace.distanceLabel === d.label;
                return (
                  <button
                    key={d.label}
                    type="button"
                    className={`fg-chip${on ? " is-on" : ""}`}
                    aria-disabled={single || undefined}
                    onClick={() => {
                      if (single) return;
                      setSelectedRace((r) => (r ? { ...r, distanceLabel: d.label } : r));
                      continueGate.clearNudge();
                    }}>
                    {d.label}
                  </button>
                );
              })}
              {/* D-13 — "Other…" replaces the stray manual-add caption link;
                  same Add-event form, distance left unset. */}
              <button
                type="button"
                className="fg-chip"
                onClick={() =>
                  openAddEvent({
                    name: selectedRace.name,
                    eventDate: selectedRace.dateYmd,
                    initialLocation: selectedRace.location ?? "",
                    initialDistanceUnset: true
                  })
                }>
                Other…
              </button>
            </div>
          </div>
        )}
        {noPlansForDistance && (
          <div className="fgp-wizard-noplan">
            We don&apos;t have a Fuel Goods training plan for this event type yet — we&apos;ll set
            up your profile next and you can add your own workouts.
          </div>
        )}
        {!selectedRace && (
          <>
            <EventSearchField
              ref={raceSearchRef}
              id={raceFieldId}
              ariaLabel="Search for your race"
              value={query}
              placeholder={
                selectedRace ? "Search to change your race…" : undefined
              }
              onChange={(next) => {
                // Pure search text. The field only shows when no race is selected
                // (picking one hides it behind the pill), so typing never clears a choice.
                setQuery(next);
                continueGate.clearNudge();
              }}
              onPickSuggested={(hit: CatalogEventDTO) => {
                const dateYmd = catalogEventStartToYmd(hit.startDate);
                // Single-distance events auto-resolve; multi-distance ones show
                // their distances inline below (in this same modal).
                setSelectedRace({
                  name: hit.name,
                  dateYmd,
                  location: formatEventLocation(hit) || undefined,
                  eventCatalogId: hit.id,
                  distanceLabel: autoDistanceLabel(hit.distances)
                });
                setRaceDistanceChoices(hit.distances);
                setQuery("");
              }}
              onOpenAddCustom={(preset) => openAddEvent(preset)}
              pinnedRaces={otherRaces}
              onPickPinned={(race) => {
                // Reuse the existing event as-is — no new profile event is
                // created (the server dedupes on its id / catalog + distance).
                setSelectedRace(race);
                setRaceDistanceChoices([]);
                setQuery("");
                continueGate.clearNudge();
              }}
              browseFromYmd={today}
            />
            {/* D-04 — left-aligned caption + quiet link, not a full-width button. */}
            <p className="fgp-wizard-race-hint" style={{ marginTop: 12 }}>
              Can&apos;t find your race?{" "}
              <button
                type="button"
                className="fgp-wz-ramplink"
                onClick={() => openAddEvent(query.trim())}>
                Add it manually
              </button>
            </p>
          </>
        )}
        <ModalValHint message={step === "race" ? continueGate.nudge : null} />
      </div>
      {selectedRace && !raceInPast && containingJourney && (
        <div className="fgp-wizard-race-exits">
          <div
            className="fgp-modal-callout fgp-modal-callout--purple fgp-wizard-race-milestone"
            style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ flex: "1 1 220px" }}>
              <strong>{selectedRace.name}</strong> lands inside your training for {" "}
              <strong>{journeyRaceName(containingJourney)}</strong>
            </span>
            <button
              type="button"
              className="fg-btn fg-btn--ghost fg-btn--sm"
              onClick={openMilestoneConfirm}>
              Add as prep race instead →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
